<template>
  <div class="action-panel-anchor">
    <div class="action-panel-anchor__slot" aria-hidden="true" />
    <section class="action-panel">
      <div class="action-panel__header">
        <p class="action-panel__title">{{ $t("assistantCatHome.actions.title") }}</p>
        <button
          type="button"
          class="action-panel__toggle"
          :aria-expanded="expanded ? 'true' : 'false'"
          :aria-label="expanded ? $t('assistantCatHome.actions.collapse') : $t('assistantCatHome.actions.expand')"
          @click="expanded = !expanded"
        >
          <icon-minimize v-if="expanded" />
          <icon-maximize v-else />
        </button>
      </div>
      <div class="action-panel__viewport" :class="{ 'is-expanded': expanded }">
        <div class="action-panel__chips">
          <template v-if="expanded">
            <button
              v-for="action in chipActions"
              :key="action"
              type="button"
              class="action-panel__chip"
              @click="$emit('action', action)"
            >
              {{ $t(`assistantCatHome.actions.${action}`) }}
            </button>
          </template>
          <div v-for="(row, rowIndex) in collapsedChipRows" v-else :key="rowIndex" class="action-panel__row">
            <button
              v-for="action in row"
              :key="action"
              type="button"
              class="action-panel__chip"
              @click="$emit('action', action)"
            >
              {{ $t(`assistantCatHome.actions.${action}`) }}
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { ASSISTANT_CAT_HOME_CHIP_ACTIONS } from "@/constants/assistantCatHome";
import IconMaximize from "~icons/figma/Maximize";
import IconMinimize from "~icons/figma/Minimize";

export default Vue.extend({
  name: "AssistantCatHomeActionPanel",
  components: {
    IconMaximize,
    IconMinimize,
  },
  data() {
    return {
      expanded: false,
      chipActions: ASSISTANT_CAT_HOME_CHIP_ACTIONS,
    };
  },
  computed: {
    collapsedChipRows(): string[][] {
      const rows: string[][] = [[], []];
      this.chipActions.forEach((action, index) => {
        rows[index % 2].push(action);
      });
      return rows;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

$chip-height: 32px;
$chip-spacing: 8px;
$collapsed-rows: 2;
$expanded-rows: 6;
$collapsed-height: $chip-height * $collapsed-rows + $chip-spacing * ($collapsed-rows - 1);
$expanded-max-height: $chip-height * $expanded-rows + $chip-spacing * ($expanded-rows - 1);
$panel-padding: 12px;

.action-panel-anchor {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.action-panel-anchor__slot {
  display: none;
}

.action-panel {
  position: relative;
  z-index: 2;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding: $panel-padding;
  overflow: hidden;
  background: var(--color-background-normal);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
}

.action-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.action-panel__title {
  margin: 0;
  color: var(--color-text-low);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);
}

.action-panel__toggle {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 23px;
  height: 23px;
  padding: 4px;
  border: none;
  border-radius: var(--radius-xs);
  background: transparent;
  color: var(--color-text-low);
  font-size: 15px;
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.action-panel__viewport {
  min-width: 0;
  height: $collapsed-height;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  transition: height 220ms ease-in-out, max-height 220ms ease-in-out;

  &.is-expanded {
    height: auto;
    max-height: $expanded-max-height;
    overflow-x: hidden;
    overflow-y: auto;
  }
}

.action-panel__chips {
  display: flex;
  flex-direction: column;
  gap: $chip-spacing;
  width: max-content;

  .is-expanded & {
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
  }
}

.action-panel__row {
  display: flex;
  flex-wrap: nowrap;
  gap: $chip-spacing;
  width: max-content;
}

.action-panel__chip {
  position: relative;
  flex: 0 0 auto;
  height: $chip-height;
  padding: 0 16px;
  border: none;
  border-radius: var(--radius-rounded);
  background: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);
  white-space: nowrap;
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}
</style>
